
import { defineComponent, PropType, reactive, toRef } from 'vue';
import GridHeaderCell from '@/components/3.6/GridHeaderCell.vue';
import GridHeaderData from '@/models/grid/GridHeaderData';
import GridHeaderCellData from '@/models/grid/GridHeaderCellData';
import { useStore } from 'vuex';
import { computed, markRaw } from '@vue/runtime-core';
import { Getters, store, mlsStore, mlsStoreV36 } from '@/store';

import DataSourceLegend from '@/components/3.6/modals/DataSourceLegend.vue';
import MapView from '@/components/3.6/modals/MapView.vue';
import AdditionalFields from '@/components/3.6/modals/AdditionalFields.vue';
import SaleHistory from '@/components/3.6/modals/SaleHistory.vue';
import HostExchangeManager from '@/manager/3.6/HostExchangeManager';
import SmartExchangeClaimsManager from '@/manager/3.6/SmartExchangeClaimsManager';
import UserSettingsManager from '@/manager/UserSettingsManager';

export default defineComponent({
    components: {
        GridHeaderCell
    },
    data() {
        return {
            modalDataSources: {
                show: false,
                component: markRaw(DataSourceLegend),
                sources: {}
            },
            modalMapView: {
                show: false,
                component: markRaw(MapView),
                details: {}
            },
            modalAdditionalFields: {
                show: false,
                component: markRaw(AdditionalFields)
            },
            modalSaleHistory: {
                show: false,
                component: markRaw(SaleHistory)
            }
        };
    },
    props: {
        rowData: {
            type: Object as PropType<GridHeaderData>,
            required: true
        }
    },
    setup() {
        const store = useStore();
        const compsData = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]);
        const isSubject = compsData.value.isSubject;

        const computedSEDetails = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
        const sEDetails = computedSEDetails.value;

        const computedMLSDetails = computed(() => mlsStore.HasProperties || mlsStoreV36.HasFeedDatas);
        const hasMLSData = computedMLSDetails.value;

        const emptySources = computed(() => store.getters[Getters.CompAddressesV36.ActiveCompEmptySourceData]);
        const emptySourceData = emptySources.value;

        const computedSubjectCascadeDisabled = computed(() => UserSettingsManager.isSubjectCascadeDisabled());
        const subjectCascadeDisabled = computedSubjectCascadeDisabled.value;

        const state = reactive({
            compsData: compsData,
            isSubject: isSubject,
            sEDetails: sEDetails,
            hasMLSData: hasMLSData,
            emptySourceData: emptySourceData,
            subjectCascadeDisabled: subjectCascadeDisabled
        });

        return {
            store,
            compsData: toRef(state, 'compsData'),
            isSubject: toRef(state, 'isSubject'),
            sEDetails: toRef(state, 'sEDetails'),
            hasMLSData: toRef(state, 'hasMLSData'),
            emptySourceData: toRef(state, 'emptySourceData'),
            subjectCascadeDisabled: toRef(state, 'subjectCascadeDisabled')
        };
    },
    created() {
        this.store.watch(
            (state, getters) => SmartExchangeClaimsManager.getCurrentClaims(),
            async () => {
                const result = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
                this.sEDetails = result.value;
            }
        );

        this.store.watch(
            (state, getters) => mlsStore.HasProperties || mlsStoreV36.HasFeedDatas,
            async () => {
                const result = computed(() => mlsStore.HasProperties || mlsStoreV36.HasFeedDatas);
                this.hasMLSData = result.value;
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.ActiveCompEmptySourceData],
            async () => {
                const result = computed(() => store.getters[Getters.CompAddressesV36.ActiveCompEmptySourceData]);
                this.emptySourceData = result.value;
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.ActiveComp],
            async () => {
                const result = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]);
                this.compsData = result;
                this.isSubject = result.value.isSubject;
            }
        );

        this.store.watch(
            (state, getters) => UserSettingsManager.isSubjectCascadeDisabled(),
            async () => {
                const result = computed(() => UserSettingsManager.isSubjectCascadeDisabled());
                this.subjectCascadeDisabled = result.value;
            }
        );
    },
    mounted() {
        (this as any).eventBus.on('launchDataSourcesModal', (e: any) => {
            this.modalDataSources.show = true;
            this.modalDataSources.sources = this.rowData;
        });
        (this as any).eventBus.on('launchMapViewModal', (e: any) => {
            this.modalMapView.show = true;
            this.modalMapView.details = this.compsData;
        });
        (this as any).eventBus.on('launchFactSheet', (e: any) => {
            HostExchangeManager.requestActiveCompFactSheet();
        });
        (this as any).eventBus.on('launchAdditionalFieldsModal', (e: any) => {
            this.modalAdditionalFields.show = true;
        });
        (this as any).eventBus.on('launchSaleHistory', (e: any) => {
            this.modalSaleHistory.show = true;
        });
    },
    computed: {
        cellDatas(): GridHeaderCellData[] {
            return this.rowData.cells;
        }
    }
});
